// KeepPileHeader Component
// Sticky header showing mini keep pile cards

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { KeepPileEntry, PersonalitySuit } from '../../../types/swipe';
import { PERSONALITY_SUITS } from '../../../constants/swipe';
import {
  KeepPileHeaderContainer,
  KeepPileHeaderContent,
  KeepPileHeaderCount,
  KeepPileCardsGroup,
  KeepPileMiniCards,
  MiniCard,
  EmptySlot,
  KeepPileHeaderActions,
  ViewKeepPileButton,
  MiniCardOverflow
} from '../../../styles/Swipe/KeepPile.styles';

interface KeepPileHeaderProps {
  entries: KeepPileEntry[];
  maxCount?: number;
  onViewKeepPile: () => void;
  /**
   * Swipes left today, or null when unlimited / not yet known. Shown here
   * rather than in the deck because this bar is already fixed and sized —
   * a counter anywhere else would cost vertical space the card needs.
   */
  swipesRemaining?: number | null;
  dailySwipeLimit?: number | null;
  unlimitedSwipes?: boolean;
}

export const KeepPileHeader: React.FC<KeepPileHeaderProps> = ({
  entries,
  maxCount = 5,
  onViewKeepPile,
  swipesRemaining = null,
  dailySwipeLimit = null,
  unlimitedSwipes = false,
}) => {
  const navigate = useNavigate();

  /**
   * How many card-shaped things the header draws.
   *
   * Capped rather than one per slot. A Premium Plus pile is 20 slots, and
   * twenty of them side by side crowded out the View button entirely — while
   * telling the reader nothing the "3/20" badge does not already say.
   */
  const MAX_VISIBLE = 6;

  const visibleEntries = entries.slice(0, MAX_VISIBLE);
  const hiddenEntryCount = entries.length - visibleEntries.length;

  // Empty slots only fill what is left of the cap, and only when there is
  // nothing hidden — "+7" and a row of empty placeholders together would be
  // contradictory.
  const emptySlots = hiddenEntryCount > 0
    ? []
    : Array(Math.max(0, Math.min(maxCount, MAX_VISIBLE) - visibleEntries.length)).fill(null);

  // Get card suit for a profile - use stored suit from swipe card, fallback to personality mapping
  const getCardSuit = (entry: KeepPileEntry): PersonalitySuit => {
    // If cardSuit is stored, use it (matches the swipe card)
    if (entry.cardSuit) {
      return entry.cardSuit;
    }
    
    // Fallback to personality type mapping for older entries
    const personalityType = entry.profile.personalityType || 'The Romantic';
    const suitMapping: Record<string, 'hearts' | 'diamonds' | 'clubs' | 'spades' | 'joker'> = {
      'The Romantic': 'hearts',
      'The Adventurer': 'spades',
      'The Thinker': 'clubs',
      'The Entrepreneur': 'diamonds',
      'The Creative': 'spades',
      'The Socialite': 'clubs',
      'The Caregiver': 'hearts',
      'The Leader': 'diamonds'
    };
    return suitMapping[personalityType] || 'hearts';
  };

  return (
    <KeepPileHeaderContainer>
      <KeepPileHeaderContent>
        {/* Count Badge - replaces title */}
        <KeepPileHeaderCount 
          $isFull={entries.length >= maxCount}
          title={`Keep Pile: ${entries.length} of ${maxCount} cards`}
        >
            {entries.length}/{maxCount}
          </KeepPileHeaderCount>

        {/* Swipes left today. Hidden when unlimited — a Premium Plus member
            has no number to watch — and while it is unknown, so it never
            shows a count that might be wrong. */}
        {!unlimitedSwipes && swipesRemaining !== null && (
          <KeepPileHeaderCount
            $isFull={swipesRemaining <= 0}
            title={dailySwipeLimit
              ? `${swipesRemaining} of ${dailySwipeLimit} swipes left today`
              : `${swipesRemaining} swipes left today`}
          >
            {swipesRemaining} ♠
          </KeepPileHeaderCount>
        )}

        {/* Group cards and button together */}
        <KeepPileCardsGroup>
        <KeepPileMiniCards>
          {/* Render kept cards as mini cards */}
          {visibleEntries.map((entry, index) => {
            const suit = getCardSuit(entry);
            return (
              <MiniCard
                key={entry.id}
                $suit={suit}
                onClick={onViewKeepPile}
                title={`${entry.profile.firstName} - Click to view`}
              >
                  <span style={{ fontSize: '1rem' }}>
                  {PERSONALITY_SUITS[suit].icon}
                </span>
              </MiniCard>
            );
          })}

          {/* Render empty slots */}
          {emptySlots.map((_, index) => (
            <EmptySlot
              key={`empty-${index}`}
              $suit="hearts"
              title="Empty slot"
            >
                <span style={{ fontSize: '0.875rem', opacity: 0.3 }}>+</span>
            </EmptySlot>
          ))}
          {hiddenEntryCount > 0 && (
            <MiniCardOverflow
              onClick={onViewKeepPile}
              title={`${hiddenEntryCount} more in your keep pile`}
            >
              +{hiddenEntryCount}
            </MiniCardOverflow>
          )}
        </KeepPileMiniCards>

        <KeepPileHeaderActions>
          <ViewKeepPileButton
            onClick={onViewKeepPile}
            disabled={entries.length === 0}
              title={entries.length > 0 ? `View Keep Pile (${entries.length} cards)` : 'Keep Pile is empty'}
          >
            View {entries.length > 0 && `(${entries.length})`}
          </ViewKeepPileButton>
        </KeepPileHeaderActions>
        </KeepPileCardsGroup>
      </KeepPileHeaderContent>
    </KeepPileHeaderContainer>
  );
};

